import APIService, { withPrefix } from "@/services/APIService";
import JSONApiService from "@/services/JSONApiService";
import {
  RECEIVER,
  APPROVER,
  ASSESSMENTS,
  AUTHOR,
  BOOKING_APPROVERS,
  COMPLIANCE_CHECKS,
  include,
  LOCATION,
  MANAGER,
  nested,
  USER,
  VERIFIER,
  REFERENCES,
  CREATED_BY,
  BASIC_BOOKING,
  CLIENT,
  EVALUATION_STATUSES,
  FUNCTION,
  BOOKING,
  ADDRESSES,
  OPTION,
  APPROVERS
} from "@/models/relationships";
import { BOOKING_MODEL } from "@/models/booking-model";
import { COMMENT_MODEL } from "@/models/comment-model";
import { disablePluralizeForCall } from "@/services/utils";
import { applicationActions } from "@/models/application-model";
import { PUBLISHING_SCHEDULE_MODEL } from "@/models/publishing-schedule-model";
import { ORGANISATION_MODEL } from "@/models/organisation-model";
import { SHARE_MODEL, USERS_FOR_SHARING } from "@/models/share-model";
import pluralize from "pluralize";
import { PUBLISHING_CHANNEL_MODEL } from "@/models/publishing-channel-model";

// TODO Remove the condition if all the cycle imports will be fixed
const API = withPrefix(APIService.client(), "bookings");

const bookingIncludes = include(
  LOCATION,
  MANAGER,
  VERIFIER,
  APPROVER,
  BOOKING_APPROVERS,
  CLIENT,
  FUNCTION
);

export const applicationIncludes = include(
  USER,
  COMPLIANCE_CHECKS,
  REFERENCES,
  ASSESSMENTS,
  ORGANISATION_MODEL,
  BOOKING,
  nested(ASSESSMENTS, CREATED_BY),
  nested(USER, ADDRESSES)
);

const callBookingAction = ({ bookingId, action, commit }) => {
  const api = JSONApiService(commit);
  api.one(BOOKING_MODEL, bookingId);
  disablePluralizeForCall(api, () => api.all(action));
  return api.post({}, bookingIncludes);
};

const bookingApi = ({ commit } = {}) => ({
  submitForApproval: async bookingId =>
    callBookingAction({ bookingId, action: "submit-for-approval", commit }),
  triggerPublishingScheduleAction: async (bookingId, unitId, action) => {
    const api = JSONApiService(commit);
    api.one(BOOKING_MODEL, bookingId);
    disablePluralizeForCall(api, () => {
      api.one("publishing-schedule", unitId);
      api.all(action);
    });
    return api.post({}, bookingIncludes);
  },
  acceptBooking: async bookingId =>
    callBookingAction({ bookingId, action: "accept", commit }),
  approveBooking: async bookingId =>
    callBookingAction({ bookingId, action: "approve", commit }),
  cancelBooking: async bookingId =>
    callBookingAction({ bookingId, action: "cancel", commit }),
  rejectBooking: async bookingId =>
    callBookingAction({ bookingId, action: "reject", commit }),
  closeBooking: async bookingId =>
    callBookingAction({ bookingId, action: "close", commit }),
  deleteBooking: async bookingId => API.delete(`${bookingId}`),
  reopenBooking: async bookingId =>
    callBookingAction({ bookingId, action: "reopen", commit }),
  createBooking: async bookingData =>
    JSONApiService(commit).create(BOOKING_MODEL, bookingData, bookingIncludes),
  editBooking: async (bookingId, editedBooking) =>
    JSONApiService(commit).update(
      BOOKING_MODEL,
      {
        id: bookingId,
        ...editedBooking
      },
      bookingIncludes
    ),
  getBooking: async bookingId =>
    JSONApiService(commit).find(BOOKING_MODEL, bookingId, bookingIncludes),
  getBookingComments: async bookingId =>
    JSONApiService(commit)
      .one(BOOKING_MODEL, bookingId)
      .all(COMMENT_MODEL)
      .get(include(AUTHOR)),
  addBookingComment: async (bookingId, comment) =>
    JSONApiService(commit)
      .one(BOOKING_MODEL, bookingId)
      .create(COMMENT_MODEL, comment),
  getBookings: async params =>
    JSONApiService(commit)
      .all(BOOKING_MODEL)
      .get({
        ...params
      }),
  getBasicBookings: async () =>
    JSONApiService(commit)
      .all(BASIC_BOOKING)
      .get(),
  getBookingCandidates: async ({ bookingId, params, includes }) =>
    JSONApiService(commit)
      .one(BOOKING_MODEL, bookingId)
      .all(applicationActions.APPLICATIONS)
      .get({ ...params, ...includes }),
  getBookingPublishingSchedule: async bookingId =>
    JSONApiService(commit)
      .one(BOOKING_MODEL, bookingId)
      .all(PUBLISHING_SCHEDULE_MODEL)
      .get(include(RECEIVER)),
  updatePublishingScheduleCost: async ({ id, data, bookingId }) => {
    return APIService.client().patch(
      `${pluralize(
        BOOKING_MODEL
      )}/${bookingId}/publishing-schedule/${id}/edit-cost`,
      { data }
    );
  },
  getBookingEvaluationStatuses: async id =>
    JSONApiService(commit)
      .one(BOOKING_MODEL, id)
      .all(EVALUATION_STATUSES)
      .get(),
  createBookingFile: async (id, data) =>
    APIService.client().postFile(
      `${pluralize(BOOKING_MODEL)}/${id}/file`,
      data
    ),
  shareBooking: async (bookingId, data) => {
    const api = JSONApiService(commit).one(BOOKING_MODEL, bookingId);
    disablePluralizeForCall(api, () => api.all(SHARE_MODEL));
    return api.patch(data);
  },
  getSharingUsers: async bookingId => {
    const api = JSONApiService(commit).one(BOOKING_MODEL, bookingId);
    disablePluralizeForCall(api, () => api.all(SHARE_MODEL));
    return api.get();
  },
  getBookingOptions: async id =>
    JSONApiService(commit)
      .one(BOOKING_MODEL, id)
      .all(OPTION)
      .get(),
  updateBookingOptions: async ({ bookingId, data }) =>
    APIService.client().patch(
      `${pluralize(BOOKING_MODEL)}/${bookingId}/options`,
      { data }
    ),
  getUsersForSharing: async bookingId =>
    API.get(`${bookingId}/${USERS_FOR_SHARING}`),
  getPublishingChannels: async params =>
    JSONApiService(commit)
      .all(PUBLISHING_CHANNEL_MODEL)
      .get({ ...params }),
  addPublishingChannel: async ({ bookingId, channel_id }) =>
    API.post(`${bookingId}/${pluralize(PUBLISHING_CHANNEL_MODEL)}`, {
      channel_id
    }),
  updateBookingApprovers: async ({ id, approvers }) =>
    APIService.client().put(`${pluralize(BOOKING_MODEL)}/${id}/${APPROVERS}`, {
      approvers
    })
});

export default bookingApi;
